import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DialogResult, PopupDialog } from '@app/Common';
import { ProvisionedApplicationsTypes } from '@app/Enums';
import { Tenant } from '@app/Models/tenant';
import { TenantEnvironmentDetails } from '@app/Models/tenant-environment-details.model';
import { TenantService } from '@app/Services';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/internal/operators/filter';
import { takeUntil, switchMap } from 'rxjs/operators';
import { CloneService } from '../../Services';
import { AsyncPipe } from '@angular/common';
import { JobStatusPlaceholderComponent } from '../../../../../Common/job-status-placeholder/job-status-placeholder.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
    selector: 'app-clone-export-only',
    templateUrl: './clone-export-only.component.html',
    styleUrls: [
        './clone-export-only.component.scss'
    ],
    imports: [JobStatusPlaceholderComponent, MatButton, MatIcon, FormsModule, MatSelect, MatOption, MatCheckbox, AsyncPipe]
})
export class CloneExportOnlyComponent implements OnInit, OnDestroy {
  @ViewChild('exportIdInput') exportIdInput: ElementRef;

  cloneInProgress$: Observable<boolean>;
  cloneFinalised$: Observable<boolean>;
  cloneFromTenantValue: string;
  cloneFromTenant: Tenant;
  cloneFromTenant$: Observable<Tenant>;
  cloneFromEnvironment$: Observable<TenantEnvironmentDetails>;
  cloneJobId$: Observable<string>;
  cloneRecords: boolean = false;

  copyButtonText = 'Copy';

  private isDestroyed$ = new Subject<void>();

  constructor(
    private tenantService: TenantService,
    private cloneService: CloneService,
    private dialog: MatDialog,
  ) {
    this.cloneInProgress$ = this.cloneService.cloneInProgress$;
    this.cloneFinalised$ = this.cloneService.cloneFinalised$;
    this.cloneFromTenant$ = this.cloneService.sourceTenant$;
    this.cloneJobId$ = this.cloneService.cloneJobId$;
  }

  ngOnInit(): void {
    this.changeCloneFrom();
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
  }

  selectText(): void {
    this.exportIdInput.nativeElement.select();
  }

  copyText(): void {
    if (!navigator?.clipboard) {
      return;
    }
    navigator.clipboard.writeText(this.exportIdInput.nativeElement.value);
    this.copyButtonText = 'Copied';
    setTimeout(() => this.copyButtonText = 'Copy', 2000);
  }

  isCloneAvailable(): boolean {
    return !!this.cloneFromTenantValue;
  }

  cloneAnotherTenant(): void {
    location.reload();
  }

  getTenantList(): Tenant[] {
    return this.tenantService.tenants.filter((tenant) => tenant.HasAccess &&
      tenant.ProvisionedApplications.includes(ProvisionedApplicationsTypes.Airsweb)
    );
  }

  changeCloneFrom(): void {
    if (!this.cloneFromTenantValue) {
      return;
    }
    this.cloneFromTenant = this.tenantService.tenants.find((tenant) =>
      tenant.Value === this.cloneFromTenantValue
    );
    this.cloneFromEnvironment$ = this.getEnvironmentForTenant(
      this.cloneFromTenantValue
    );
  }

  openCloneConfirmationPopup(): void {
    const dialogRef = this.createConfirmationPopup();
    dialogRef.afterClosed().pipe(
        filter((result) => !!result && result.Result),
        switchMap(() => this.cloneFromEnvironment$),
        switchMap((cloneFromEnvironment) =>
          this.cloneService.cloneExportOnlyRequest(this.cloneFromTenant, cloneFromEnvironment.EnvironmentId, this.cloneRecords)
        ),
        takeUntil(this.isDestroyed$)
      )
      .subscribe();
  }

  private createConfirmationPopup(): MatDialogRef<PopupDialog, DialogResult> {
    const dialogRef = this.dialog.open<PopupDialog, any, DialogResult>(PopupDialog);
    dialogRef.componentInstance.DialogTitle = 'Clone Tenant Export Only';
    dialogRef.componentInstance.DialogDescription =
      `Are you sure you want to Clone tenant <b>${this.cloneFromTenant.Value}</b>?`;
    dialogRef.componentInstance.DialogConfirmButtonText = 'Confirm';
    dialogRef.componentInstance.DialogCancelButtonText = 'Cancel';

    return dialogRef;
  }

  private getEnvironmentForTenant(tenantName: string): Observable<TenantEnvironmentDetails> {
    return this.tenantService
      .getEnvironmentDetailsByTenant(tenantName)
      .pipe(
        filter((environemntDetails: TenantEnvironmentDetails | null) => environemntDetails !== null)
      );
  }
}
